/**
 * GFM pipe tables for the table block (spec §7.4.16): parse a pasted table (or tab-separated lines) into
 * headers, alignments and cells, and write a table back as GFM. Cells are inline Markdown, kept as
 * written; only a pipe is escaped, and line breaks become spaces.
 */
import type { Align, TableState } from "./model";

export interface ParsedTable {
  headers: string[];
  aligns: Align[];
  rows: string[][];
}

/** Split one table line into cells: outer pipes optional, `\|` is a literal pipe. */
function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const out: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (ch === "|") {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const DELIMITER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

function align(spec: string): Align {
  const s = spec.trim();
  const left = s.startsWith(":");
  const right = s.endsWith(":");
  return left && right ? "center" : right ? "right" : left ? "left" : null;
}

/** A GFM table (header line, delimiter line, rows), or null when `text` is not one. */
export function parseGfm(text: string): ParsedTable | null {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2 || !lines[0]!.includes("|") || !DELIMITER.test(lines[1]!)) return null;
  const headers = cells(lines[0]!);
  const aligns = cells(lines[1]!).map(align);
  const width = headers.length;
  const rows = lines.slice(2).map((l) => {
    const r = cells(l).slice(0, width);
    while (r.length < width) r.push("");
    return r;
  });
  while (aligns.length < width) aligns.push(null);
  return { headers, aligns: aligns.slice(0, width), rows };
}

/** Tab-separated lines (a spreadsheet copy) as a grid, or null for a single line without tabs. */
export function parseTsv(text: string): string[][] | null {
  const lines = text.replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n");
  if (lines.length < 2 && !lines[0]?.includes("\t")) return null;
  return lines.map((l) => l.split("\t").map((c) => c.trim()));
}

const cell = (v: string) => v.replace(/\r?\n/g, " ").replace(/\|/g, "\\|");

/** The table as GFM: header row, alignment row, one line per row. */
export function toGfm(table: Pick<TableState, "columns" | "rows">): string {
  const cols = table.columns;
  if (cols.length === 0) return "";
  const line = (vals: string[]) => `| ${vals.map(cell).join(" | ")} |`;
  const delim = cols.map((c) => (c.align === "center" ? ":---:" : c.align === "right" ? "---:" : c.align === "left" ? ":---" : "---"));
  return [line(cols.map((c) => c.header)), `| ${delim.join(" | ")} |`, ...table.rows.map((r) => line(cols.map((c) => r.cells[c.id] ?? "")))].join("\n") + "\n";
}
